import { useState, useEffect } from 'react';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Shirt, CreditCard, Plus, Package, Clock, CheckCircle, Truck, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { compressImageDataUrl } from '../lib/imageUtils';

const SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

export default function RequestsPage() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [requestType, setRequestType] = useState('uniform');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [form, setForm] = useState({ uniform_size: '', uniform_quantity: 1, notes: '' });
  const [idPhoto, setIdPhoto] = useState(null);
  const [photoDialog, setPhotoDialog] = useState({ open: false, src: null, empName: '' });

  const isManager = ['super_admin', 'management', 'branch_manager'].includes(user?.system_role);

  const viewIdCardPhoto = async (requestId) => {
    try {
      const res = await api.get(`/requests/${requestId}`);
      const r = res.data;
      if (!r.photo_base64) {
        toast.error('No photo uploaded for this request.');
        return;
      }
      const src = r.photo_base64.startsWith('data:') ? r.photo_base64 : `data:image/jpeg;base64,${r.photo_base64}`;
      setPhotoDialog({ open: true, src, empName: r.employee_name || 'Employee' });
    } catch (err) {
      toast.error('Could not load photo');
    }
  };

  const fetchData = async () => {
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterType !== 'all') params.request_type = filterType;
      const res = await api.get('/requests', { params });
      setRequests(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [filterStatus, filterType]);

  const handleSubmit = async () => {
    try {
      const payload = {
        request_type: requestType,
        uniform_size: requestType === 'uniform' ? form.uniform_size : null,
        uniform_quantity: requestType === 'uniform' ? parseInt(form.uniform_quantity) : null,
        photo_base64: requestType === 'id_card' ? idPhoto : null,
        notes: form.notes
      };
      await api.post('/requests', payload);
      toast.success(`${requestType === 'uniform' ? 'Uniform' : 'ID Card'} request submitted`);
      setDialogOpen(false);
      setForm({ uniform_size: '', uniform_quantity: 1, notes: '' });
      setIdPhoto(null);
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/requests/${id}/status`, { status });
      toast.success(`Request marked as ${status}`);
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handlePhotoCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await compressImageDataUrl(file);
      setIdPhoto(compressed);
    } catch {
      toast.error('Could not process photo');
    }
  };

  const statusConfig = {
    requested: { color: 'bg-amber-100 text-amber-700', icon: Clock },
    processing: { color: 'bg-blue-100 text-blue-700', icon: Package },
    delivered: { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
    denied: { color: 'bg-red-100 text-red-700', icon: XCircle },
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="requests-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-black text-neutral-950 text-2xl sm:text-3xl tracking-tight">Requests</h1>
          <p className="text-neutral-500 text-sm mt-1">Uniform & ID Card requests</p>
        </div>
        <div className="flex gap-3">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-36 rounded-sm" data-testid="req-filter-status">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="requested">Requested</SelectItem>
              <SelectItem value="processing">Processing</SelectItem>
              <SelectItem value="delivered">Delivered</SelectItem>
              <SelectItem value="denied">Denied</SelectItem>
            </SelectContent>
          </Select>
          <Button className="bg-red-600 hover:bg-red-700 text-white rounded-sm" onClick={() => setDialogOpen(true)} data-testid="new-request-button">
            <Plus className="h-4 w-4 mr-2" /> New Request
          </Button>
        </div>
      </div>

      {/* Requests Table */}
      <Card className="border border-neutral-200 shadow-sm rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-neutral-50">
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Type</TableHead>
                {isManager && <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Employee</TableHead>}
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Details</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Status</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 hidden sm:table-cell">Date</TableHead>
                {isManager && <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [1,2,3].map(i => <TableRow key={i}><TableCell colSpan={6}><div className="h-8 bg-neutral-100 rounded animate-pulse" /></TableCell></TableRow>)
              ) : requests.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-neutral-500">
                  <Shirt className="h-8 w-8 mx-auto mb-2 text-neutral-300" /> No requests found
                </TableCell></TableRow>
              ) : requests.map(r => {
                const sc = statusConfig[r.status] || statusConfig.requested;
                return (
                  <TableRow key={r.id} className="hover:bg-neutral-50" data-testid={`request-row-${r.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {r.request_type === 'uniform' ? <Shirt className="h-4 w-4 text-blue-500" /> : <CreditCard className="h-4 w-4 text-purple-500" />}
                        <span className="text-sm font-medium capitalize">{r.request_type?.replace('_', ' ')}</span>
                      </div>
                    </TableCell>
                    {isManager && <TableCell className="text-sm text-neutral-700">{r.employee_name}</TableCell>}
                    <TableCell className="text-sm text-neutral-800">
                      {r.request_type === 'uniform' ? `Size: ${r.uniform_size || '-'}, Qty: ${r.uniform_quantity || 1}` : 'ID Card'}
                      {r.notes && <span className="text-xs text-neutral-600 block">{r.notes}</span>}
                      {r.request_type === 'id_card' && isManager && (
                        <Button variant="link" size="sm" className="p-0 h-auto text-xs text-blue-700 underline"
                          onClick={() => viewIdCardPhoto(r.id)} data-testid={`view-photo-${r.id}`}>
                          View employee photo
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={`text-xs capitalize ${sc.color}`}>{r.status}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-neutral-500 hidden sm:table-cell">
                      {new Date(r.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                    </TableCell>
                    {isManager && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {r.status === 'requested' && (
                            <>
                              <Button size="sm" variant="ghost" className="text-xs text-blue-600 h-7"
                                onClick={() => updateStatus(r.id, 'processing')} data-testid={`process-req-${r.id}`}>
                                <Package className="h-3 w-3 mr-1" /> Process
                              </Button>
                              <Button size="sm" variant="ghost" className="text-xs text-red-600 h-7"
                                onClick={() => updateStatus(r.id, 'denied')} data-testid={`deny-req-${r.id}`}>
                                <XCircle className="h-3 w-3 mr-1" /> Deny
                              </Button>
                            </>
                          )}
                          {r.status === 'processing' && (
                            <>
                              <Button size="sm" variant="ghost" className="text-xs text-emerald-600 h-7"
                                onClick={() => updateStatus(r.id, 'delivered')} data-testid={`deliver-req-${r.id}`}>
                                <Truck className="h-3 w-3 mr-1" /> Delivered
                              </Button>
                              <Button size="sm" variant="ghost" className="text-xs text-red-600 h-7"
                                onClick={() => updateStatus(r.id, 'denied')} data-testid={`deny-req-${r.id}`}>
                                <XCircle className="h-3 w-3 mr-1" /> Deny
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* New Request Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="new-request-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">New Request</DialogTitle>
            <DialogDescription>Request uniform or ID card from HR</DialogDescription>
          </DialogHeader>

          <Tabs value={requestType} onValueChange={setRequestType}>
            <TabsList className="w-full rounded-sm">
              <TabsTrigger value="uniform" className="flex-1 rounded-sm" data-testid="tab-uniform">
                <Shirt className="h-3.5 w-3.5 mr-1.5" /> Uniform
              </TabsTrigger>
              <TabsTrigger value="id_card" className="flex-1 rounded-sm" data-testid="tab-id-card">
                <CreditCard className="h-3.5 w-3.5 mr-1.5" /> ID Card
              </TabsTrigger>
            </TabsList>

            <TabsContent value="uniform" className="mt-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium">Size</Label>
                  <Select value={form.uniform_size} onValueChange={v => setForm(p => ({ ...p, uniform_size: v }))}>
                    <SelectTrigger className="rounded-sm" data-testid="uniform-size-select">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      {SIZES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium">Quantity</Label>
                  <Input type="number" min={1} value={form.uniform_quantity}
                    onChange={e => setForm(p => ({ ...p, uniform_quantity: e.target.value }))}
                    className="rounded-sm" data-testid="uniform-quantity-input" />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="id_card" className="mt-4 space-y-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Photo for ID Card</Label>
                <label className="block border-2 border-dashed border-neutral-300 rounded-sm p-4 text-center cursor-pointer hover:border-red-400">
                  <input type="file" accept="image/*" capture="user" onChange={handlePhotoCapture} className="hidden" data-testid="id-photo-input" />
                  {idPhoto ? (
                    <img src={idPhoto} alt="ID Photo" className="max-h-32 mx-auto rounded" />
                  ) : (
                    <p className="text-xs text-neutral-400">Tap to take or upload photo</p>
                  )}
                </label>
              </div>
            </TabsContent>
          </Tabs>

          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Notes (optional)</Label>
            <Textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              rows={2} className="rounded-sm" placeholder="Any special requirements..." data-testid="request-notes-input" />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSubmit} className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="submit-request-button">
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── ID Card Photo Viewer ── */}
      <Dialog open={photoDialog.open} onOpenChange={(o) => !o && setPhotoDialog({ open: false, src: null, empName: '' })}>
        <DialogContent className="max-w-md" data-testid="id-photo-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">ID Card Photo</DialogTitle>
            <DialogDescription>{photoDialog.empName}</DialogDescription>
          </DialogHeader>
          {photoDialog.src && (
            <div className="py-2">
              <img src={photoDialog.src} alt="ID Card" className="w-full rounded-md border border-neutral-200" />
              <a href={photoDialog.src} download={`${photoDialog.empName}_id.jpg`}
                 className="inline-flex items-center mt-3 text-xs text-blue-700 hover:underline">
                Download full-size
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
